import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil, Plus, RefreshCw } from "lucide-react"

import { DataState } from "@/components/DataState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { formatAmount, formatDate, formatPercent } from "@/lib/format"
import { calcularMrr } from "@/lib/mrr"
import { BILLING_MODEL_LABELS, type Contract, type ContractLine } from "@/lib/types"
import { onWriteError } from "@/lib/writeErrors"

import {
  ContractLinesEditor,
  lineaNueva,
  problemaDeLineas,
} from "@/routes/masters/ContractLinesEditor"
import { FieldsDialog, type FieldSpec, type FieldValue } from "@/routes/masters/FieldsDialog"
import { fetchClients, fetchContracts, saveContract } from "@/routes/masters/api"
import { t } from "@/lib/i18n"

export function ContractsPage() {
  const { can } = useRbac()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState<Contract | null>(null)
  const [creating, setCreating] = React.useState(false)
  /*
    Las lineas viven APARTE de los campos del formulario.

    `FieldsDialog` modela campos escalares y las lineas son N filas de cinco
    columnas. Meterlas en `FieldSpec` habria vuelto generico algo que se usa en
    una sola pantalla, y los otros seis maestros habrian cargado con esa
    complejidad para siempre.
  */
  const [lines, setLines] = React.useState<ContractLine[]>([])

  function abrir(contract: Contract | null) {
    setLines(contract ? contract.lines.map((l) => ({ ...l })) : [lineaNueva()])
    setEditing(contract)
    setCreating(contract === null)
  }

  const contractsQuery = useQuery({ queryKey: ["contracts"], queryFn: fetchContracts })
  const clientsQuery = useQuery({ queryKey: ["clients"], queryFn: fetchClients })

  const mutation = useMutation({
    mutationFn: (payload: {
      values: Record<string, FieldValue>
      lines: ContractLine[]
      reason: string
      id?: string
    }) =>
      saveContract({
        data: { ...(payload.values as Partial<Contract>), lines: payload.lines, id: payload.id },
        reason: payload.reason,
      }),
    onSuccess: () => {
      toast({
        title: "Contrato guardado",
        description: "El revenue schedule y la proyeccion se regeneran en el acto.",
      })
      setEditing(null)
      setCreating(false)
      void queryClient.invalidateQueries({ queryKey: ["contracts"] })
    },
    onError: onWriteError("el contrato"),
  })

  const contracts = contractsQuery.data ?? []
  /*
    El calculo vive en `lib/mrr.ts` y no aca, por la misma razon por la que
    `paisDeLaPantalla` vive en `lib/entities.ts`: es una regla de negocio con
    dos filtros que no son obvios —se suma por LINEA y solo cuentan las
    VIGENTES— y probarla exige poder llamarla sin montar React.
  */
  const { vigente: mrr, noVigente: mrrNoVigente, vencidosActivos } = calcularMrr(
    contracts,
    new Date().toISOString().slice(0, 10),
  )

  const fields: FieldSpec[] = [
    {
      type: "select",
      name: "clientId",
      label: "Cliente",
      required: true,
      wide: true,
      // Sólo los clientes ACTIVOS: un contrato nuevo con un cliente dado de baja es justo lo que la baja evita.
      options: (clientsQuery.data ?? []).filter((client) => client.activo).map((client) => ({
        value: client.id,
        label: client.name,
      })),
    },
    { type: "text", name: "code", label: "Codigo de contrato", required: true },
    /*
      El modelo de facturacion y el importe NO son campos del contrato: son de
      cada LINEA, y estan en el editor de abajo. Tenerlos aca duplicados
      obligaba a decidir cual manda cuando no coincidieran.
    */
    {
      type: "select",
      name: "currency",
      label: "Moneda",
      required: true,
      options: [
        { value: "PEN", label: "PEN" },
        { value: "USD", label: "USD" },
      ],
    },
    {
      type: "select",
      name: "indexation",
      label: "Indexacion",
      options: [
        { value: "ninguna", label: "Ninguna" },
        { value: "ipc", label: "IPC" },
        { value: "usd", label: "Tipo de cambio USD" },
      ],
    },
    { type: "date", name: "startDate", label: "Inicio", required: true },
    { type: "date", name: "endDate", label: "Fin", required: true },
    {
      type: "select",
      name: "renewal",
      label: "Renovacion",
      options: [
        { value: "automatica", label: "Automatica" },
        { value: "manual", label: "Manual" },
      ],
    },
    {
      type: "number",
      name: "renewalProbability",
      label: "Probabilidad de renovacion (0-1)",
      help: "Pondera la capa de renovaciones en la proyeccion de ingresos.",
    },
    {
      type: "number",
      name: "paymentTermsDays",
      label: "Termino de pago propio (dias)",
      help: "Solo si difiere del termino del cliente.",
    },
  ]

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="flex max-w-[80ch] items-start gap-1.5 text-xs leading-[1.15rem] text-muted-foreground">
          <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t("Al guardar, el revenue schedule y la proyeccion se regeneran en el acto. El contrato es lo que convierte una expectativa comercial en una venta proyectada.")}</p>
        <Button size="sm" disabled={!can("edit")} onClick={() => abrir(null)}>
          <Plus />
          {t("Nuevo contrato")}</Button>
      </div>

      <div className="overflow-hidden rounded-panel border bg-surface shadow-xs">
        <DataState
          loading={contractsQuery.isLoading}
          error={contractsQuery.error}
          onRetry={() => void contractsQuery.refetch()}
          empty={contracts.length === 0}
          emptyMessage={t("Todavia no hay contratos registrados.")}
          emptyHint={t("Sin contratos, la proyeccion de ingresos solo puede extrapolar lo facturado. Con contratos sabe que se renueva, cuando y con que probabilidad.")}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">{t("Codigo")}</TableHead>
                <TableHead>{t("Cliente")}</TableHead>
                <TableHead className="w-44">{t("Modelo")}</TableHead>
                <TableHead className="w-20">{t("Moneda")}</TableHead>
                <TableHead className="w-32 text-right">{t("Importe")}</TableHead>
                <TableHead className="w-24">{t("Indexacion")}</TableHead>
                <TableHead className="w-24">{t("Inicio")}</TableHead>
                <TableHead className="w-24">{t("Fin")}</TableHead>
                <TableHead className="w-28">{t("Renovacion")}</TableHead>
                <TableHead className="w-24 text-right">{t("Prob.")}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contracts.map((contract) => (
                <TableRow key={contract.id}>
                  <TableCell className="code">{contract.code}</TableCell>
                  <TableCell className="font-medium">{contract.clientName}</TableCell>
                  <TableCell className="text-2xs">
                    {/*
                      Con varias lineas se DICE que son varias en vez de mostrar
                      el modelo de una: «Por hitos» en un contrato que ademas
                      lleva una cuota mensual es una media verdad.
                    */}
                    {contract.lines.length > 1
                      ? `${contract.lines.length} líneas`
                      : BILLING_MODEL_LABELS[contract.billingModel]}
                  </TableCell>
                  <TableCell>{contract.currency}</TableCell>
                  <TableCell className="num">{formatAmount(contract.amount)}</TableCell>
                  <TableCell className="text-2xs">{contract.indexation}</TableCell>
                  <TableCell className="code text-2xs">
                    {formatDate(contract.startDate)}
                  </TableCell>
                  <TableCell className="code text-2xs">{formatDate(contract.endDate)}</TableCell>
                  <TableCell>
                    <Badge variant={contract.renewal === "automatica" ? "success" : "outline"}>
                      {contract.renewal}
                    </Badge>
                  </TableCell>
                  <TableCell className="num">
                    {formatPercent(contract.renewalProbability, 0)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={!can("edit")}
                      onClick={() => abrir(contract)}
                    >
                      <Pencil />
                      {t("Editar")}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={4} className="text-2xs uppercase">
                  {t("MRR corriendo hoy (recurrente fijo, vigente)")}</TableCell>
                <TableCell className="num">{formatAmount(mrr)}</TableCell>
                <TableCell colSpan={6} className="text-2xs text-muted-foreground">
                  {mrrNoVigente > 0 ? (
                    <span>
                      {t("Quedan fuera")}{" "}<span className="num-strong">{formatAmount(mrrNoVigente)}</span>{" "}
                      de lineas recurrentes que ya terminaron o no han empezado: no son ingreso
                      recurrente hoy.{" "}
                    </span>
                  ) : null}
                  {vencidosActivos > 0 ? (
                    <span>
                      {t("Y hay")}{" "}<strong>{vencidosActivos}</strong> {" "}{t("contrato(s) marcados activos con la vigencia vencida — o se renovaron y falta actualizar la fecha, o habria que cerrarlos.")}</span>
                  ) : null}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </DataState>
      </div>

      <FieldsDialog
        open={Boolean(editing) || creating}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null)
            setCreating(false)
          }
        }}
        title={editing ? `Editar contrato · ${editing.code}` : "Nuevo contrato"}
        description={t("Cliente, lineas, modelo de facturacion, moneda e indexacion, vigencia y renovacion.")}
        fields={fields}
        extra={
          <ContractLinesEditor
            lines={lines}
            onChange={setLines}
            currency={editing?.currency ?? "PEN"}
          />
        }
        blockedReason={problemaDeLineas(lines) ?? undefined}
        initial={
          editing
            ? {
                clientId: editing.clientId,
                code: editing.code,
                currency: editing.currency,
                indexation: editing.indexation,
                startDate: editing.startDate,
                endDate: editing.endDate,
                renewal: editing.renewal,
                renewalProbability: editing.renewalProbability,
                paymentTermsDays: editing.paymentTermsDays,
              }
            : {
                currency: "PEN",
                indexation: "ninguna",
                renewal: "automatica",
                renewalProbability: 0.8,
              }
        }
        pending={mutation.isPending}
        reasonSuggestions={[
          "Renovacion de contrato firmada por el cliente.",
          "Ajuste de tarifa acordado en adenda.",
        ]}
        onSubmit={(values, reason) =>
          mutation.mutateAsync({ values, lines, reason, id: editing?.id }).then(() => undefined)
        }
      />
    </div>
  )
}
